import { useTheme } from '@emotion/react';
import { Box, useMediaQuery } from '@mui/material';
import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import ReactQuill from 'react-quill';
import Quill from 'quill';
import "react-quill/dist/quill.snow.css";
import CustomLabel from '../CustomLabel';
import { VisuallyHiddenInput } from 'ui-components/VisuallyHiddenInput';
import { useFormikContext } from 'formik';
import { useIsRequired } from './RequiredFieldsContext';
import {
  countRichTextCharacters,
  countRichTextWords,
} from 'utilities/richText';

// forwardRef, not a plain function component: callers (Settings > Deal Flow's email
// template chips) hold a ref to this editor and reach the Quill instance through it to
// insert content at the cursor. A plain function component silently drops the ref, so
// `ref.current` stayed null and every chip click was a no-op.
const CustomRichTextEditor = forwardRef(({
  label,
  name,
  placeholder,
  value,
  handleChange,
  labelStyle = {},
  maxLength = 500,
  // 'characters' (default) or 'words' — what maxLength is measured in.
  countBy = 'characters',
  readOnly = false,
  required = false,
  // Pulled out of ...props so it can be merged with the defaults below rather than
  // replacing them wholesale - a caller that passes `formats` must not have to remember
  // to re-list "chip" (or the list formats) to keep them working.
  formats,
  ...props
}, ref) => {
  const countsWords = countBy === 'words';
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const quillRef = useRef(null);

  // The public handle. `getEditor()` matches react-quill's own API, which is what the
  // existing callers already expect (`someRef.current?.getEditor?.()`); `insertChip`
  // wraps the insert-at-cursor dance so callers don't have to repeat it.
  useImperativeHandle(ref, () => ({
    getEditor: () => quillRef.current?.getEditor(),
    insertChip: (chipText) => {
      const editor = quillRef.current?.getEditor();
      if (!editor) return;
      // getSelection(true) focuses the editor first, so a chip clicked while the editor
      // was never focused still lands somewhere sane rather than being dropped.
      const selection = editor.getSelection(true);
      const index = selection ? selection.index : editor.getLength() - 1;
      editor.insertEmbed(index, 'chip', chipText, 'user');
      editor.insertText(index + 1, ' ', 'user');
      editor.setSelection(index + 2, 0, 'user');
      editor.focus();
    },
  }), []);

  const { errors, touched, setFieldValue, values } = useFormikContext();
  // Fall back to the Yup schema so required fields are marked automatically.
  const autoRequired = useIsRequired(name);
  const isRequired = required || autoRequired;

  // useEffect(() => {
  //   if (errors[name] && touched[name] && quillRef.current) {
  //     quillRef.current.focus();
  //   }
  // }, [errors, touched, name]);

  // const getPlainText = (html) => {
  //   const tmp = document.createElement('div');
  //   tmp.innerHTML = html;
  //   return tmp.textContent || tmp.innerText || "";
  // };

  // The single source of truth for "how much has been written", shared with the
  // Yup rules that gate submission, so the counter and the validator agree.
  const countUnits = (text) =>
    countsWords ? countRichTextWords(text) : countRichTextCharacters(text);

  const handleTextChange = (newValue, delta, source, editor) => {
    setRemainingCount(maxLength - countUnits(editor.getText()));
    handleChange(newValue);
  };

  const handleKeyDown = (e) => {
    // Allow arrow keys and navigation keys to work normally
    if (
      ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)
    ) {
      return;
    }

    if (e.key === 'Backspace' || e.key === 'Delete') {
      return; // Allow Backspace and Delete
    }

    // Check if user presses Enter
    if (e.key === 'Enter') {
      if (countsWords) {
        // A newline starts no word on its own; only block it once full.
        if (remainingCount <= 0) e.preventDefault();
        return;
      }

      const newCharacterCount = countUnits(value || "");

      // Reduce character count when Enter is pressed
      if (newCharacterCount + 1 <= maxLength) {
        // Only allow Enter if character count is still within the limit
        setRemainingCount((prevCount) => prevCount - 1);
        return; // Allow Enter
      } else {
        e.preventDefault(); // Prevent Enter if at character limit
      }
    }

    // Prevent typing once the limit is reached. At a word limit, only whitespace
    // is blocked — otherwise the final word could never be finished.
    if (remainingCount <= 0 && !e.ctrlKey) {
      if (!countsWords || e.key === ' ') {
        e.preventDefault();
      }
    }

    // Other key combinations (e.g., Ctrl+A, Ctrl+V)
    if (e.ctrlKey && (e.key === 'a' || e.key === 'A')) {
      return; // Allow Ctrl + A
    }

    if (e.ctrlKey && (e.key === 'v' || e.key === 'V')) {
      return;
      // const clipboardData = e?.clipboardData || window?.clipboardData;
      // console.log(clipboardData,"clipboardData")
      // const pastedData = clipboardData?.getData('Text');
      // console.log(pastedData,"pastedData")
      // const plainText = getPlainText(value || "");
      // console.log(plainText,"plainText")
      // const remaining = maxLength - plainText?.length;

      // if (remaining <= 0) {
      // e.preventDefault();
      //   return;
      // }

      // if (pastedData?.length > remaining) {
      //   e.preventDefault();
      // const truncatedData = pastedData?.slice(0, maxLength);
      // handleChange(truncatedData);
      // }
    }
  };



  const [remainingCount, setRemainingCount] = useState(
    maxLength - countUnits(value || "")
  );

  // Update the remaining count whenever the value changes externally
  useEffect(() => {
    setRemainingCount(maxLength - countUnits(value || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, maxLength, countBy]);

  // const handleTextChange = (newValue, delta, source, editor) => {
  //   const plainText = editor.getText(); // Gets plain text without HTML
  //   const newCharacterCount = plainText.trim()?.length;

  //   if (newCharacterCount <= maxLength) {
  //     setCharacterCount(maxLength - newCharacterCount);
  //     handleChange(newValue);
  //   } else {
  //     // Prevent exceeding the character limit by reverting to the previous value
  //     handleChange(value);
  //   }
  // };

  // const handleKeyDown = (e) => {
  //   // Allow arrow keys and other navigation keys to work normally
  //   if (
  //     ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)
  //   ) {
  //     return;
  //   }

  //   if (e.key === 'Backspace' || e.key === 'Delete') {
  //     return; // Allow Backspace and Delete
  //   }

  //   if(e.key !== 'Enter'){
  //     e.preventDefault();
  //   }

  //   // If character count is zero and the key is not Ctrl, Enter, or Shift, prevent further input
  //   if (characterCount <= 0 && !e.ctrlKey && e.key !== 'Enter' && e.key !== 'Shift') {
  //     e.preventDefault();
  //   }

  //   if (e.ctrlKey && (e.key === 'a' || e.key === 'A')) {
  //     return; // Allow Ctrl + A
  //   }

  //   if (e.ctrlKey && (e.key === 'v' || e.key === 'V')) {
  //     // Handle paste via keyboard shortcut
  //     const clipboardData = e?.clipboardData || window?.clipboardData;
  //     const pastedData = clipboardData?.getData('Text');
  //     const plainText = getPlainText(value || "");
  //     const remaining = maxLength - plainText?.length;

  //     if (remaining <= 0) {
  //       e.preventDefault();
  //       return;
  //     }

  //     if (pastedData?.length > remaining) {
  //       e.preventDefault();
  //       const truncatedData = pastedData?.slice(0, remaining);
  //       handleChange(truncatedData);
  //     }
  //   }

  //   // Block the default Enter behavior unless Shift is pressed (if applicable)
  //   if (!e.shiftKey && e.key === 'Enter') {
  //     e.preventDefault();
  //   }
  // };


  // const handlePaste = (e) => {
  //   e.preventDefault();
  //   const clipboardData = e.clipboardData || window.clipboardData;
  //   let pastedData = clipboardData.getData('Text');

  //   // Function to get the plain text without HTML tags
  //   const getPlainText = (html) => {
  //     const tempDiv = document.createElement("div");
  //     tempDiv.innerHTML = html;
  //     return tempDiv.textContent || tempDiv.innerText || "";
  //   };

  //   const plainText = getPlainText(value || "");
  //   const remaining = maxLength - plainText?.length;

  //   if (remaining <= 0) {
  //     return; // Do nothing if there is no remaining character space
  //   }

  //   // Trim pasted data if it exceeds the remaining character limit
  //   if (pastedData?.length > remaining) {
  //     pastedData = pastedData.slice(0, remaining);
  //   }

  //   // Insert the trimmed text at the current cursor position
  //   const editor = quillRef.current.getEditor();
  //   const range = editor.getSelection();
  //   if (range) {
  //     editor.insertText(range.index, pastedData);
  //     handleChange(editor.root.innerHTML); // Update the value with the new content
  //   }
  // };


  // useEffect(() => {
  //   const editor = quillRef.current.getEditor();
  //   const editorRoot = editor.root;
  //   editorRoot.addEventListener('paste', handlePaste);

  //   return () => {
  //     editorRoot.removeEventListener('paste', handlePaste);
  //   };
  // }, [value, characterCount]);

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    const editorRoot = editor?.root;
    const handleDrop = (e) => {
      if (e.dataTransfer && e.dataTransfer.files.length > 0) {
        // Prevent drag and drop if the file is an image
        const file = e.dataTransfer.files[0];
        if (file.type.startsWith("image/")) {
          e.preventDefault();
        }
      }
    };

    // Chips are contenteditable=false + user-select:none, so a click/double-click on one
    // leaves Quill with no selection and the toolbar has nothing to format. Select the
    // chip (length 1 embed) on click so bold / italic / underline apply to it.
    const handleChipClick = (e) => {
      const chipNode = e.target.closest?.('.ql-chip-badge');
      if (!chipNode || !editor.isEnabled()) return;
      const blot = Quill.find(chipNode);
      if (!blot) return;
      editor.setSelection(editor.getIndex(blot), 1, 'user');
    };

    editorRoot.addEventListener('drop', handleDrop);
    editorRoot.addEventListener('click', handleChipClick);

    return () => {
      editorRoot.removeEventListener('drop', handleDrop);
      editorRoot.removeEventListener('click', handleChipClick);
    };
  }, []);

  const [editorHeight] = React.useState(
    isSmallScreen || isMediumScreen ? '170px' : '150px'
  )

  // Quill silently discards any embed whose format is not on this list, so "chip" is
  // always appended - a caller narrowing the formats (the Subject box allows only bold /
  // italic / underline) must not lose the chips along with the rest.
  const quillFormats = React.useMemo(
    () => Array.from(new Set([
      ...(Array.isArray(formats) && formats.length
        ? formats
        : ["bold", "italic", "underline", "list", "bullet", "indent", "align"]),
      "chip",
    ])),
    [formats]
  );

  return (
    <>
      <div style={{ position: 'relative', width: '100%' }}>
        {label && (
          <CustomLabel
            required={isRequired}
            sx={{
              color: theme.palette.customColors.g500,
              marginBottom: '8px',
              display: 'block',
              fontSize: "0.875rem",
              ...labelStyle,
            }}
          >
            {label}
          </CustomLabel>
        )}

        {/* Editor box: positioning context for the counter so it always hugs
            the box's bottom-right edge, regardless of whether a label is shown */}
        <div style={{ position: 'relative', width: '100%' }}>
          <div style={{ resize: 'vertical', overflowY: 'auto', minHeight: editorHeight, height: editorHeight, maxWidth: '100%' }}>
          <ReactQuill
            ref={quillRef}
            style={{
              height: "100%",
              background: theme.palette.customColors.whiteBG,
              overflowY: 'auto',
              maxWidth: '100%',
            }}
            readOnly={readOnly}
            modules={{
              toolbar: {
                container: [
                  // [{ header: "1" }, { header: "2" }],
                  ["bold", "italic", "underline"],
                  [{ list: "ordered" }, { list: "bullet" }],
                  [{ indent: "-1" }, { indent: "+1" }],
                ],
              },
              // Disable the link tool to prevent adding URLs that may exceed character count
              clipboard: {
                matchVisual: false,
              },
            }}
            formats={quillFormats}
            placeholder={placeholder}
            onChange={handleTextChange}
            // value={value}
            value={values[name]}
            onKeyDown={handleKeyDown}
            {...props}
          />
          </div>

          {!readOnly && (
            <div
              className="character_count"
              style={{
                right: '0.25rem',
                position: 'absolute',
                bottom: '0.4rem',
                color: remainingCount < 0 ? "red" : theme.palette.customColors.g500,
                background: theme.palette.customColors.whiteBG,
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '0.75rem',
                lineHeight: 1,
                pointerEvents: 'none',
              }}
            >
              {remainingCount} {!isSmallScreen
                ? (countsWords
                    ? remainingCount === 1 ? "word " : "words "
                    : remainingCount === 1 ? "character " : "characters ") + "left"
                : ""}
            </div>
          )}
        </div>

      </div>
    </>
  );
});

CustomRichTextEditor.displayName = 'CustomRichTextEditor';

export default CustomRichTextEditor;
